import React, { useState, useEffect /*, useRef */ } from "react";
import { Stack, TextField } from "@fluentui/react";
import styles from "./QuestionInput.module.css";
import {
  IconButton,
  CLEAR_ICONS,
  MIC_ICONS,
  UPLOAD_ICONS,
  SEND_ICONS,
  STOP_ICONS,
} from "../IconButton";
import DocumentUploadDialog from "../DocumentUploadDialog/DocumentUploadDialog";
import { FileStatus } from "../DocumentUploadDialog/DocumentUploadDialog";
import DocumentLoadingIcon from "../HeaderButtons/DocumentLoadingIcon";
import { UserUploadConfig } from "../../pages/chat/types";
import { ChatMessage } from "../../api";

interface Props {
  onSend: (question: string) => void;
  onMicrophoneClick: () => void;
  isListening: boolean;
  isSilenceWarning?: boolean;
  micError?: string;
  micDevices: MediaDeviceInfo[];
  selectedMicDeviceId: string | undefined;
  onMicDeviceChange: (deviceId: string | undefined) => void;
  questionToSend: string;
  setQuestionToSend: (question: string) => void;
  placeholder?: string;
  clearOnSend?: boolean;
  conversationId: string;
  onRefetchConversation?: () => void;
  onFileDeleted?: (uploadedFileName: string) => void;
  onUploadedFilesChange?: (files: FileStatus[]) => void;
  resetQuestionToSend: () => void;
  questionToSendId?: string;
  uploadedFiles: FileStatus[];
  isLoadingFiles?: boolean;
  userUploadConfig?: UserUploadConfig;
  answers: ChatMessage[];
  isGenerating?: boolean;
  onStopGenerating?: () => void;
}

export const QuestionInput = ({
  onSend,
  onMicrophoneClick,
  isListening,
  isSilenceWarning,
  micError,
  micDevices,
  selectedMicDeviceId,
  onMicDeviceChange,
  questionToSend,
  setQuestionToSend,
  placeholder,
  clearOnSend,
  conversationId,
  onRefetchConversation,
  onFileDeleted,
  onUploadedFilesChange,
  resetQuestionToSend,
  questionToSendId,
  uploadedFiles,
  isLoadingFiles = false,
  userUploadConfig,
  answers,
  isGenerating,
  onStopGenerating,
}: Props) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const [fileStatuses, setFileStatuses] = useState<FileStatus[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const uploadedCount = fileStatuses.filter(
    (status) => status.status === "completed",
  ).length;
  const totalFiles = fileStatuses.length;

  useEffect(() => {
    setFileStatuses([]);
    setIsUploading(false);
    setIsDialogOpen(false);
  }, [conversationId]);

  useEffect(() => {
    setFileStatuses(uploadedFiles ?? []);
  }, [uploadedFiles]);

  const sendQuestion = () => {
    if (isGenerating || !questionToSend.trim()) {
      return;
    }

    onSend(questionToSend);

    if (clearOnSend) {
      setQuestionToSend("");
    }
  };

  const onEnterPress = (ev: React.KeyboardEvent<Element>) => {
    if (ev.key === "Enter" && ev.ctrlKey) {
      ev.preventDefault();
      sendQuestion();
    }
  };

  const onClearQuestion = () => {
    resetQuestionToSend();
  };

  const onQuestionChange = (
    _ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue?: string,
  ) => {
    setQuestionToSend(newValue || "");
  };

  const handleUploadProgress = (statuses: FileStatus[], uploading: boolean) => {
    setFileStatuses(statuses);
    onUploadedFilesChange?.(statuses);
    setIsUploading(uploading);
  };

  const openDialog = () => setIsDialogOpen(true);
  const closeDialog = () => setIsDialogOpen(false);

  return (
    <Stack className={styles.questionInputContainer}>
      <div className={styles.questionInputTextAreaWrapper}>
        <TextField
          style={{ backgroundColor: "white" }}
          className={styles.questionInputTextArea}
          placeholder={placeholder}
          multiline
          resizable={false}
          borderless
          value={questionToSend}
          onChange={(e, newValue) => {
            if (newValue !== undefined) {
              onQuestionChange(e, newValue);
            }
          }}
          onKeyDown={onEnterPress}
        />
        <div className={styles.cancelContainer}>
          {(questionToSendId || questionToSend) && (
            <IconButton
              label="Clear"
              icons={CLEAR_ICONS}
              size="xsmall"
              onClick={onClearQuestion}
            />
          )}
        </div>
      </div>
      <div className={styles.cancelUploadAndSendContainer}>
        <div className={styles.uploadAndSendContainer}>
          <div className={styles.microphoneWrapper}>
            {micError && <div className={styles.micError}>{micError}</div>}
            <IconButton
              label={isListening ? "Stop" : "Dictate"}
              icons={MIC_ICONS}
              size="xsmall"
              disabled={isGenerating}
              onClick={onMicrophoneClick}
              className={
                !isGenerating && isListening
                  ? `${styles.micRecording} ${isSilenceWarning ? styles.micSilenceWarning : ""}`
                  : undefined
              }
              menu={
                micDevices.length > 1
                  ? {
                      label: "Select microphone",
                      items: [
                        {
                          key: "default",
                          label: "Default",
                          selected: !selectedMicDeviceId,
                          onSelect: () => onMicDeviceChange(undefined),
                        },
                        ...micDevices.map((device) => ({
                          key: device.deviceId,
                          label:
                            device.label ||
                            `Microphone ${device.deviceId.slice(0, 6)}`,
                          selected: selectedMicDeviceId === device.deviceId,
                          onSelect: () => onMicDeviceChange(device.deviceId),
                        })),
                      ],
                    }
                  : undefined
              }
            />
          </div>

          <IconButton
            label="Upload files"
            icons={UPLOAD_ICONS}
            onClick={openDialog}
            size="xsmall"
            content={
              isUploading || isLoadingFiles ? (
                <DocumentLoadingIcon />
              ) : undefined
            }
            badge={
              isLoadingFiles
                ? "0/..."
                : isUploading
                  ? `${uploadedCount}/${totalFiles}`
                  : uploadedCount > 0
                    ? totalFiles
                    : undefined
            }
          />

          {isGenerating && onStopGenerating ? (
            <IconButton
              label="Stop generating"
              icons={STOP_ICONS}
              size="xsmall"
              onClick={onStopGenerating}
            />
          ) : (
            <IconButton
              label="Send"
              icons={SEND_ICONS}
              size="xsmall"
              onClick={sendQuestion}
            />
          )}
        </div>

        <DocumentUploadDialog
          isOpen={isDialogOpen}
          onDismiss={closeDialog}
          conversationId={conversationId}
          onUploadProgress={handleUploadProgress}
          onUploadComplete={onRefetchConversation ?? (() => {})}
          onFileDeleted={onFileDeleted}
          uploadedFiles={uploadedFiles}
          userUploadConfig={userUploadConfig}
          answers={answers}
        />
      </div>
    </Stack>
  );
};
